import {
  CartesianScaleTypeRegistry,
  Chart,
  ChartEvent,
  ScaleOptionsByType,
  ScriptableContext
} from 'chart.js'
import { _DeepPartialObject } from 'chart.js/dist/types/utils'

import {
  formatAxisValue,
  formatValue
} from 'components_new/organisms/Widget/Types/utils'

import { getTheme } from 'themes'

import { TChartClick, TChartData, TChartDataset, TChartVariant } from '../types'

export type TBubblePoint = { label: string; r: number; x: number; y: number }

export const AXIS = { R: 2, X: 0, Y: 1 } as const

export const getBubblePoints = (data: TChartData): TBubblePoint[] => {
  const xValues = (data.datasets[AXIS.X]?.values ?? []) as (number | null)[]
  const yValues = (data.datasets[AXIS.Y]?.values ?? []) as (number | null)[]
  const rValues = (data.datasets[AXIS.R]?.values ?? []) as (number | null)[]

  return data.labels.map((label, index) => ({
    label,
    r: rValues[index] ?? 0,
    x: xValues[index] ?? 0,
    y: yValues[index] ?? 0
  }))
}

/*-- scales --*/
export const getBubbleChartScalesOptions = (params: {
  data: TChartData
  variant: TChartVariant
}):
  | _DeepPartialObject<{
      [key: string]: ScaleOptionsByType<keyof CartesianScaleTypeRegistry>
    }>
  | undefined => {
  const { data, variant } = params

  const theme = getTheme('light')

  // A reference line outside the data would fall off the axis unseen.
  const axis = (dataset?: TChartDataset, lines: { value: number }[] = []) => ({
    beginAtZero: variant === 'detailed',
    ...(lines.length > 0
      ? {
          suggestedMax: Math.max(...lines.map((line) => line.value)),
          suggestedMin: Math.min(...lines.map((line) => line.value))
        }
      : null),
    border: {
      display: variant === 'detailed'
    },
    grid: {
      color: theme.palette.divider,
      display: variant === 'detailed'
    },
    ticks: {
      callback: (value: string | number) =>
        typeof value === 'number' ? formatAxisValue(value, null) : value,
      color: theme.palette.text.secondary,
      display: variant !== 'minimal'
    },
    title: {
      color: theme.palette.text.secondary,
      display: variant === 'detailed' && Boolean(dataset?.label),
      text: dataset?.label
    },
    type: 'linear' as const
  })

  return {
    x: axis(data.datasets[AXIS.X], data.verticalLines),
    y: axis(data.datasets[AXIS.Y], data.horizontalLines)
  }
}

/*-- radius --*/
export const getRDomain = (
  points: TBubblePoint[]
): { rMax: number; rMin: number } => {
  const values = points.map((point) => point.r)

  if (!values.length) return { rMax: 0, rMin: 0 }

  return { rMax: Math.max(...values), rMin: Math.min(...values) }
}

const MIN_NORMALIZED_RADIUS = 0.25
const MIN_RADIUS_PX = 4

export const getBubbleRadius = (params: {
  ctx: ScriptableContext<'bubble'>
  rMax: number
  rMin: number
  value: number
}): number => {
  const { ctx, rMax, rMin, value } = params

  const scale = 0.01 * ctx.chart.width
  const normalized = rMax === rMin ? 1 : (value - rMin) / (rMax - rMin)

  return Math.max(
    Math.max(normalized, MIN_NORMALIZED_RADIUS) * scale,
    MIN_RADIUS_PX
  )
}

/*-- formatting --*/
const hasRValues = (data: TChartData): boolean =>
  (data.datasets[AXIS.R]?.values ?? []).some(
    (value) => value !== null && value !== undefined
  )

export const getBubbleRows = (params: {
  data: TChartData
  decimals?: number
  point: TBubblePoint
  significantFigures?: number
}): { label: string; value: string }[] => {
  const { data, decimals, point, significantFigures } = params

  const format = (value: number) =>
    formatValue(
      value,
      null,
      decimals ?? 1,
      undefined,
      undefined,
      significantFigures
    )

  const axisRow = (
    dataset: TChartDataset | undefined,
    value: number,
    fallbackLabel: string
  ) => {
    const unit = dataset?.unit ?? ''

    return {
      label: dataset?.label ?? fallbackLabel,
      // "1 887 tkr", but "12%" without a space, as in ValueText
      value: `${format(value)}${unit && unit !== '%' ? ' ' : ''}${unit}`
    }
  }

  const rows = [
    axisRow(data.datasets[AXIS.X], point.x, 'x'),
    axisRow(data.datasets[AXIS.Y], point.y, 'y')
  ]

  if (hasRValues(data)) {
    rows.push(axisRow(data.datasets[AXIS.R], point.r, 'r'))
  }

  return rows
}

/*-- click handling --*/
export const handleOnSegmentClick = (params: {
  callback: (params: TChartClick) => void
  chart: Chart
  event: ChartEvent
  points: TBubblePoint[]
}) => {
  const { callback, chart, event, points } = params

  const native = (event as any)?.native

  if (!native) return

  const rect = chart.canvas.getBoundingClientRect()

  const left = rect.left + native.offsetX + window.scrollX
  const top = rect.top + native.offsetY + window.scrollY

  const hits = chart.getElementsAtEventForMode(
    event as unknown as Event,
    'nearest',
    { intersect: true },
    true
  )

  if (!hits.length) return

  const { datasetIndex } = hits[0]
  const point = points[datasetIndex]

  if (!point) return

  callback({
    datasetIndex,
    index: datasetIndex,
    label: point.label,
    position: { left, top },
    value: point.r
  })
}
